import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { AxiosError } from 'axios';
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  History,
  Loader2,
  Pencil,
  Play,
  Plus,
  SearchX,
  Send,
  Star,
  Trash2,
  TrendingUp,
  X,
  XCircle,
} from 'lucide-react';
import type { GoalDetail, GoalStatus } from '@pms/shared';
import { PERMISSIONS } from '@pms/shared';
import { goalsService } from '@/services/goals.service';
import { usePermission } from '@/hooks/usePermission';
import { useAuth } from '@/hooks/useAuth';
import { getErrorMessage } from '@/lib/errors';
import { formatDate, formatDateTime, formatRelativeTime } from '@/lib/dateFormat';
import { cn } from '@/lib/utils';
import { SideDrawer } from '@/components/tasks/SideDrawer';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  GoalPriorityBadge,
  GoalRiskBadge,
  GoalStatusBadge,
  GoalTargetTypeBadge,
} from '@/components/goals/GoalBadges';
import {
  formatDueLabel,
  formatGoalDelta,
  formatGoalValue,
  progressLockReason,
} from '@/components/goals/goalConstants';

interface Props {
  goalId: string | null;
  onClose: () => void;
  onEdit: (goal: GoalDetail) => void;
  onLogProgress: (goal: GoalDetail) => void;
  onReview: (goal: GoalDetail) => void;
}

/** Human labels for the audit-log action codes the backend writes. */
const AUDIT_LABEL: Record<string, string> = {
  created: 'Goal created',
  updated: 'Details edited',
  status_changed: 'Status changed',
  progress_logged: 'Progress logged',
  milestone_done: 'Milestone completed',
  milestone_reopened: 'Milestone reopened',
  milestone_added: 'Milestone added',
  milestone_removed: 'Milestone removed',
  reviewed: 'Reviewed',
  auto_missed: 'Marked missed automatically',
  deleted: 'Goal deleted',
};

function MetaRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <span className="shrink-0 text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-right text-sm text-foreground">{value}</span>
    </div>
  );
}

export function GoalDetailDrawer({ goalId, onClose, onEdit, onLogProgress, onReview }: Props) {
  const queryClient = useQueryClient();
  const perms = usePermission();
  const { user } = useAuth();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [newMilestone, setNewMilestone] = useState('');

  const detailQuery = useQuery({
    queryKey: ['goals', 'detail', goalId],
    queryFn: () => goalsService.getGoal(goalId!),
    enabled: !!goalId,
    // A 4xx here is a settled answer (deleted, or outside this user's scope), so
    // retrying only repeats a request that can never succeed.
    retry: (failureCount, error) => {
      const status = (error as AxiosError).response?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 1;
    },
  });

  /** Deep links (?open=… from a notification, or a stale URL) can point at a goal
   *  this user may not see — that needs an explanation, not an error with Retry. */
  const isUnavailable = [403, 404].includes(
    (detailQuery.error as AxiosError | undefined)?.response?.status ?? 0,
  );

  const goal = detailQuery.data;
  const isOwner = !!goal && goal.user_id === user?.id;

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['goals'] });
  }

  const statusMutation = useMutation({
    mutationFn: (status: Extract<GoalStatus, 'active' | 'submitted' | 'cancelled'>) =>
      goalsService.changeStatus(goal!.id, { status }),
    onSuccess: (_data, status) => {
      toast.success(
        status === 'submitted' ? 'Goal submitted for review'
          : status === 'cancelled' ? 'Goal cancelled'
            : 'Goal activated',
      );
      invalidate();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const milestoneMutation = useMutation({
    mutationFn: (vars: { milestoneId: string; isDone: boolean }) =>
      goalsService.toggleMilestone(goal!.id, vars.milestoneId, { is_done: vars.isDone }),
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const addMilestoneMutation = useMutation({
    mutationFn: (title: string) => goalsService.addMilestone(goal!.id, { title }),
    onSuccess: () => {
      setNewMilestone('');
      invalidate();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const removeMilestoneMutation = useMutation({
    mutationFn: (milestoneId: string) => goalsService.deleteMilestone(goal!.id, milestoneId),
    onSuccess: () => {
      toast.success('Milestone removed');
      invalidate();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: () => goalsService.deleteGoal(goal!.id),
    onSuccess: () => {
      toast.success('Goal deleted');
      // Drop the detail query outright instead of invalidating it — a blanket
      // invalidate would refetch the goal we just deleted and 404.
      queryClient.removeQueries({ queryKey: ['goals', 'detail', goalId] });
      void queryClient.invalidateQueries({ queryKey: ['goals', 'list'] });
      void queryClient.invalidateQueries({ queryKey: ['goals', 'summary'] });
      onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const canRecord = !!goal
    && (isOwner || perms.has(PERMISSIONS.GOALS_EDIT) || perms.has(PERMISSIONS.GOALS_ASSIGN))
    && perms.has(PERMISSIONS.GOALS_PROGRESS_UPDATE);
  const canEdit = !!goal && perms.has(PERMISSIONS.GOALS_EDIT)
    && goal.status !== 'achieved' && goal.status !== 'cancelled';
  const canReview = !!goal && perms.has(PERMISSIONS.GOALS_REVIEW)
    && goal.status === 'submitted' && !isOwner;
  const isMilestone = goal?.target_type === 'milestone';
  /** Non-null when the goal's status blocks progress edits — doubles as the tooltip. */
  const lockReason = goal ? progressLockReason(goal.status) : null;
  const canWorkOnProgress = canRecord && !lockReason;

  return (
    <>
      <SideDrawer
        open={!!goalId}
        onOpenChange={(v) => !v && onClose()}
        title="Goal details"
        description="Target, progress, milestones, review outcome and activity for this goal."
      >
        {detailQuery.isLoading ? (
          <div className="flex h-full items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : isUnavailable ? (
          <div className="flex flex-col items-center gap-3 px-6 py-20 text-center">
            <SearchX className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm font-medium text-foreground">This goal isn&apos;t available</p>
            <p className="max-w-xs text-xs leading-relaxed text-muted-foreground">
              It may have been deleted, or it belongs to an employee outside the goals
              you are allowed to see.
            </p>
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          </div>
        ) : detailQuery.isError ? (
          <div className="flex flex-col items-center gap-3 py-20 text-center text-destructive">
            <AlertTriangle className="h-8 w-8 opacity-60" />
            <p className="text-sm font-medium">Failed to load this goal</p>
            <Button variant="outline" size="sm" onClick={() => void detailQuery.refetch()}>Retry</Button>
          </div>
        ) : !goal ? (
          <div className="py-20 text-center text-sm text-muted-foreground">Goal not found</div>
        ) : (
          <div className="space-y-5 p-4 pr-12 sm:p-5 sm:pr-14">
            {/* Hero */}
            <div className="rounded-xl bg-gradient-to-br from-primary to-teal-700 p-4 text-white shadow-card">
              <div className="flex flex-wrap items-center gap-1.5">
                <GoalStatusBadge status={goal.status} className="bg-white/20 text-white" />
                <GoalTargetTypeBadge type={goal.target_type} className="bg-white/20 text-white" />
                <GoalRiskBadge
                  isOverdue={goal.is_overdue}
                  isAtRisk={goal.is_at_risk}
                  className="bg-white/20 text-white"
                />
              </div>
              <h2 className="mt-2.5 text-lg font-semibold leading-snug tracking-tight">{goal.title}</h2>
              <p className="mt-1 text-xs text-white/75">
                {goal.owner_name ?? '—'}
                {goal.department_name ? ` · ${goal.department_name}` : ''}
              </p>
              <div className="mt-3.5">
                <div className="h-2 w-full overflow-hidden rounded-full bg-white/20">
                  <div
                    className="h-full rounded-full bg-white transition-all duration-700"
                    style={{ width: `${Math.min(100, Math.max(0, goal.progress_percent))}%` }}
                  />
                </div>
                <div className="mt-1.5 flex items-center justify-between text-[11px] text-white/80">
                  <span>
                    {isMilestone
                      ? `${goal.milestone_done} of ${goal.milestone_total} milestones`
                      : `${formatGoalValue(goal.current_value, goal.target_type, null)} of ${formatGoalValue(goal.target_value, goal.target_type, goal.unit)}`}
                  </span>
                  <span className="font-semibold tabular-nums text-white">{goal.progress_percent}%</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              {canRecord && goal.status === 'draft' && (
                <Button size="sm" onClick={() => statusMutation.mutate('active')} disabled={statusMutation.isPending}>
                  <Play className="h-4 w-4" /> Start Goal
                </Button>
              )}
              {canWorkOnProgress && !isMilestone && (
                <Button size="sm" onClick={() => onLogProgress(goal)}>
                  <TrendingUp className="h-4 w-4" /> Log Progress
                </Button>
              )}
              {canRecord && goal.status === 'active' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => statusMutation.mutate('submitted')}
                  disabled={statusMutation.isPending}
                >
                  <Send className="h-4 w-4" /> Submit for Review
                </Button>
              )}
              {canRecord && goal.status === 'missed' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => statusMutation.mutate('active')}
                  disabled={statusMutation.isPending}
                >
                  <Play className="h-4 w-4" /> Reopen
                </Button>
              )}
              {canReview && (
                <Button size="sm" onClick={() => onReview(goal)}>
                  <CheckCircle2 className="h-4 w-4" /> Review
                </Button>
              )}
              {canEdit && (
                <Button size="sm" variant="outline" onClick={() => onEdit(goal)}>
                  <Pencil className="h-4 w-4" /> Edit
                </Button>
              )}
              {canRecord && (goal.status === 'draft' || goal.status === 'active' || goal.status === 'submitted') && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setConfirmCancel(true)}
                  disabled={statusMutation.isPending}
                >
                  <XCircle className="h-4 w-4" /> Cancel Goal
                </Button>
              )}
              {perms.has(PERMISSIONS.GOALS_DELETE) && (
                <Button size="sm" variant="outline" onClick={() => setConfirmDelete(true)}>
                  <Trash2 className="h-4 w-4 text-destructive" /> Delete
                </Button>
              )}
            </div>

            {goal.description && (
              <div className="rounded-lg border border-border/60 bg-card p-3">
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">{goal.description}</p>
              </div>
            )}

            {/* Facts */}
            <div className="rounded-lg border border-border/60 bg-card px-3 py-1.5 divide-y divide-border/40">
              <MetaRow label="Category" value={goal.category_name ?? 'Uncategorized'} />
              <MetaRow label="Priority" value={<GoalPriorityBadge priority={goal.priority} />} />
              {!isMilestone && (
                <MetaRow
                  label="Target"
                  value={`${formatGoalValue(goal.start_value, goal.target_type, null)} → ${formatGoalValue(goal.target_value, goal.target_type, goal.unit)}`}
                />
              )}
              <MetaRow label="Timeline" value={`${formatDate(goal.start_date)} → ${formatDate(goal.due_date)}`} />
              <MetaRow
                label="Deadline"
                value={
                  <span className={cn(goal.is_overdue && 'text-rose-600 dark:text-rose-400')}>
                    {formatDueLabel(goal.due_date, goal.status) || formatDate(goal.due_date)}
                  </span>
                }
              />
              <MetaRow label="Weight" value={`${goal.weight} / 100`} />
              <MetaRow label="Assigned by" value={goal.assigner_name ?? 'Self-set'} />
              {goal.completed_at && <MetaRow label="Completed" value={formatDateTime(goal.completed_at)} />}
            </div>

            {/* Review outcome */}
            {goal.reviewed_at && (
              <div className="rounded-lg border border-border/60 bg-card p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Manager review
                  </span>
                  {goal.review_rating !== null && (
                    <span className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((r) => (
                        <Star
                          key={r}
                          className={cn(
                            'h-3.5 w-3.5',
                            goal.review_rating! >= r ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30',
                          )}
                        />
                      ))}
                    </span>
                  )}
                </div>
                {goal.review_comment && (
                  <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">{goal.review_comment}</p>
                )}
                <p className="mt-2 text-[11px] text-muted-foreground">
                  {goal.reviewer_name ?? 'Reviewer'} · {formatDateTime(goal.reviewed_at)}
                </p>
              </div>
            )}

            {/* Milestones */}
            {isMilestone && (
              <div className="rounded-lg border border-border/60 bg-card">
                <div className="flex items-center gap-2 border-b border-border/40 px-3 py-2 text-sm font-semibold text-primary">
                  <ClipboardList className="h-4 w-4" /> Milestones
                  <span className="ml-auto text-xs font-normal text-muted-foreground">
                    {goal.milestone_done} / {goal.milestone_total} done
                  </span>
                </div>
                {canRecord && lockReason && (
                  <p className="border-b border-border/40 bg-muted/30 px-3 py-1.5 text-[11px] text-muted-foreground">
                    {lockReason}
                  </p>
                )}
                <ul className="divide-y divide-border/40">
                  {goal.milestones.map((m) => (
                    <li key={m.id} className="flex items-start gap-2.5 px-3 py-2.5">
                      <Checkbox
                        className="mt-0.5"
                        checked={m.is_done}
                        disabled={!canRecord || !!lockReason || milestoneMutation.isPending}
                        aria-label={m.title}
                        title={lockReason ?? undefined}
                        onChange={(e) =>
                          milestoneMutation.mutate({ milestoneId: m.id, isDone: e.target.checked })
                        }
                      />
                      <div className="min-w-0 flex-1">
                        <p className={cn('text-sm', m.is_done && 'text-muted-foreground line-through')}>
                          {m.title}
                        </p>
                        {m.is_done && m.done_at && (
                          <p className="text-[11px] text-muted-foreground">
                            {m.done_by_name ?? 'Someone'} · {formatRelativeTime(m.done_at)}
                          </p>
                        )}
                      </div>
                      {canEdit && goal.milestones.length > 1 && (
                        <button
                          type="button"
                          aria-label={`Remove milestone ${m.title}`}
                          disabled={removeMilestoneMutation.isPending}
                          onClick={() => removeMilestoneMutation.mutate(m.id)}
                          className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive disabled:opacity-50"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>

                {canEdit && (
                  <form
                    className="flex items-center gap-2 border-t border-border/40 px-3 py-2.5"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const title = newMilestone.trim();
                      if (title) addMilestoneMutation.mutate(title);
                    }}
                  >
                    <Input
                      value={newMilestone}
                      onChange={(e) => setNewMilestone(e.target.value)}
                      placeholder="Add a milestone…"
                      maxLength={200}
                      className="h-9"
                    />
                    <Button
                      type="submit"
                      size="sm"
                      variant="outline"
                      disabled={!newMilestone.trim() || addMilestoneMutation.isPending}
                    >
                      {addMilestoneMutation.isPending
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <Plus className="h-4 w-4" />}
                      Add
                    </Button>
                  </form>
                )}
              </div>
            )}

            {/* Progress history */}
            <div className="rounded-lg border border-border/60 bg-card">
              <div className="flex items-center gap-2 border-b border-border/40 px-3 py-2 text-sm font-semibold text-primary">
                <TrendingUp className="h-4 w-4" /> Progress history
                {goal.progress_updates.length > 0 && (
                  <span className="ml-auto text-xs font-normal text-muted-foreground">
                    {goal.progress_updates.length} {goal.progress_updates.length === 1 ? 'update' : 'updates'}
                  </span>
                )}
              </div>
              {goal.progress_updates.length === 0 ? (
                <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                  No updates logged yet
                </p>
              ) : (
                <ul className="max-h-72 divide-y divide-border/40 overflow-y-auto">
                  {goal.progress_updates.map((u) => (
                    <li key={u.id} className="px-3 py-2.5">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-sm font-medium text-foreground">
                          {/* Entries are additive, so a row shows the movement it made and
                              the total it landed on. A removed milestone leaves its history
                              row behind with a null link, so it needs a label of its own. */}
                          {u.milestone_title
                            ?? (u.value !== null
                              ? (
                                <>
                                  <span className={u.value < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}>
                                    {formatGoalDelta(u.value, goal.target_type, goal.unit)}
                                  </span>
                                  {u.value_after !== null && (
                                    <span className="font-normal text-muted-foreground">
                                      {' → '}
                                      {formatGoalValue(u.value_after, goal.target_type, goal.unit)}
                                    </span>
                                  )}
                                </>
                              )
                              : 'Milestone update (item since removed)')}
                        </span>
                        <span className="shrink-0 text-[11px] text-muted-foreground">
                          {formatDate(u.logged_on)}
                        </span>
                      </div>
                      {u.note && <p className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">{u.note}</p>}
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        {u.logged_by_name ?? 'Someone'} · {formatRelativeTime(u.created_at)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Audit trail */}
            <div className="rounded-lg border border-border/60 bg-card">
              <div className="flex items-center gap-2 border-b border-border/40 px-3 py-2 text-sm font-semibold text-primary">
                <History className="h-4 w-4" /> Activity
                <span className="ml-auto text-xs font-normal text-muted-foreground">
                  {goal.audit_log.length} {goal.audit_log.length === 1 ? 'entry' : 'entries'}
                </span>
              </div>
              {/* Long-lived goals accumulate a lot of history — keep each log in its own
                  scroll area so the drawer stays navigable. */}
              <ul className="max-h-72 divide-y divide-border/40 overflow-y-auto">
                {goal.audit_log.map((a) => (
                  <li key={a.id} className="flex items-baseline justify-between gap-2 px-3 py-2">
                    <span className="min-w-0 text-xs text-foreground">
                      {AUDIT_LABEL[a.action] ?? a.action}
                      <span className="text-muted-foreground"> · {a.actor_name ?? 'System'}</span>
                    </span>
                    <span className="shrink-0 text-[11px] text-muted-foreground">
                      {formatRelativeTime(a.created_at)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </SideDrawer>

      <ConfirmDialog
        open={confirmCancel}
        onOpenChange={setConfirmCancel}
        title="Cancel this goal?"
        description="It stops being tracked and can no longer be edited or reopened."
        confirmLabel="Cancel Goal"
        cancelLabel="Keep It"
        onConfirm={() => statusMutation.mutate('cancelled')}
      />

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete this goal?"
        description="The goal and its progress history are removed from all views."
        confirmLabel="Delete"
        onConfirm={() => deleteMutation.mutate()}
      />
    </>
  );
}
